import React, { useState } from "react";
import SignatureUploadWidget from "./utils/SignatureUploadWidget";
import { useDataStore } from "@store/user_org";
import { useNotification } from "../../../context/NotificationContext";
import { FieldData } from "./utils/DraggableResizableField";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Configure PDF.js worker - using unpkg.com with .mjs for ES modules support
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FillSignatureProps {
  pdfUrl: string;
  initialFields?: FieldData[];
  onClose: () => void;
  onSign: (fields: FieldData[], dimensions: { width: number; height: number }) => Promise<void>;
}

/**
 * FillSignature Component
 *
 * Displays a PDF for the user to sign. No fields are shown - the PDF is already prepared.
 * User only uploads/updates their signature and clicks "Signer".
 *
 * Usage:
 * ```tsx
 * <FillSignature
 *   pdfUrl={preparedPdfUrl}
 *   onClose={() => setOpen(false)}
 *   onSign={async (fields, dimensions) => {
 *     const response = await signAPI(preparedPdfUrl, fields, dimensions);
 *     setSignedPdfUrl(response.pdf_url);
 *   }}
 * />
 * ```
 */
const FillSignature: React.FC<FillSignatureProps> = ({
  pdfUrl,
  initialFields = [],
  onClose,
  onSign,
}) => {
  const userData = useDataStore((state) => state.userData);
  const { showNotification } = useNotification();
  const [loadingData, setLoadingData] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 1190.56, height: 1683.78 });
  const [pageWidth, setPageWidth] = useState(800);
  // Visual zoom for the PDF viewer. 1 = 100%. Range 0.5–2.5 keeps things
  // readable without melting the layout. Coordinates aren't affected
  // because there's nothing to place — the fields are already frozen.
  const [zoom, setZoom] = useState(1);
  const ZOOM_STEP = 0.2;
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2.5;
  const zoomedWidth = pageWidth * zoom;

  const hasSignature = !!userData?.infos?.signature_id;

  const doSign = async () => {
    if (!userData?.infos?.signature_id) return;

    setLoadingData(true);
    try {
      const fields = initialFields.map((field) => ({
        ...field,
        type: field.type === "Texte" ? "Text" : field.type,
        content: field.type === "Signature" ? userData.infos.signature_id : field.content,
      }));

      await onSign(fields, pdfDimensions);
    } catch (error) {
      console.error("Error signing document:", error);
      showNotification({ message: "Échec de la signature du document", type: "error" });
    } finally {
      setLoadingData(false);
    }
  };

  const handleSign = async () => {
    if (!hasSignature) {
      showNotification({ message: "Veuillez ajouter votre signature avant de signer", type: "error" });
      return;
    }
    await doSign();
  };

  // Auto-sign: when the user just added their signature for the first
  // time from within this flow, proceed directly to signing without
  // making them click "Signer" again.
  const autoSignTriggered = React.useRef(false);
  const handleSignatureAdded = React.useCallback(() => {
    // The widget calls fetchUserData which updates userData.infos.signature_id.
    // We set a flag here; the effect below picks it up on the next render
    // once the store has the new signature.
    autoSignTriggered.current = true;
  }, []);

  React.useEffect(() => {
    if (autoSignTriggered.current && hasSignature && !loadingData) {
      autoSignTriggered.current = false;
      doSign();
    }
  }, [hasSignature]);

  const onDocumentLoadSuccess = async ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);

    // Get actual PDF dimensions with scale 2 (matching Hexasign)
    try {
      const loadingTask = pdfjs.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const firstPage = await pdf.getPage(1);
      const viewport = firstPage.getViewport({ scale: 2 });
      setPdfDimensions({ width: viewport.width, height: viewport.height });
      setPageWidth(viewport.width);
    } catch (error) {
      console.error("Error getting PDF dimensions:", error);
    }
  };

  return (
    <div className="h-full w-full rounded-lg bg-white overflow-hidden">
      <div className="flex h-full w-full">
        {/* Sidebar for signature upload */}
        <div className="w-96 p-6 h-full overflow-auto bg-white border-r border-gray-200 flex-shrink-0">
          <div className="flex flex-col gap-4 h-full">
            <div className="text-lg font-semibold">Signer le document</div>

            {!hasSignature ? (
              <>
                <div className="text-sm text-gray-600">
                  Dessinez ou importez votre signature pour signer ce document.
                  Elle sera sauvegardée pour vos prochaines signatures.
                </div>
                <SignatureUploadWidget
                  prominent
                  onSignatureUpdated={handleSignatureAdded}
                />
              </>
            ) : (
              <>
                <div className="text-sm text-gray-600">
                  Votre signature sera automatiquement ajoutée au document.
                </div>
                <SignatureUploadWidget onSignatureUpdated={handleSignatureAdded} />
              </>
            )}

            <div className="flex flex-col gap-3 mt-auto">
              <button
                type="button"
                className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 text-base"
                onClick={onClose}
              >
                Annuler
              </button>
              {hasSignature && (
                <button
                  type="button"
                  className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-base"
                  onClick={handleSign}
                  disabled={loadingData}
                >
                  {loadingData ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Signature en cours...
                    </div>
                  ) : (
                    "Signer"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* PDF Viewer */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#f0f0f0",
            minWidth: 0,
            position: "relative",
          }}
        >
          {/* Zoom toolbar */}
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              display: "flex",
              justifyContent: "center",
              gap: 8,
              padding: "8px 12px",
              backgroundColor: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(4px)",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
              disabled={zoom <= ZOOM_MIN}
              className="px-3 py-1 rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
              aria-label="Réduire"
              title="Réduire"
            >
              −
            </button>
            <button
              type="button"
              onClick={() => setZoom(1)}
              className="px-3 py-1 rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 text-sm tabular-nums"
              aria-label="Réinitialiser le zoom"
              title="Réinitialiser"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
              disabled={zoom >= ZOOM_MAX}
              className="px-3 py-1 rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
              aria-label="Agrandir"
              title="Agrandir"
            >
              +
            </button>
          </div>

          <div style={{ flex: 1, overflow: "auto", position: "relative" }}>
            <div
              style={{
                position: "relative",
                width: `${zoomedWidth}px`,
                margin: "0 auto",
              }}
            >
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(error) => {
                  console.error("Error loading PDF:", error);
                  showNotification({ message: "Erreur lors du chargement du PDF", type: "error" });
                }}
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <div
                    key={`page_${index + 1}`}
                    style={{
                      position: "relative",
                      width: `${zoomedWidth}px`,
                      marginBottom: `${10 * zoom}px`,
                    }}
                  >
                    <Page
                      pageNumber={index + 1}
                      width={zoomedWidth}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </div>
                ))}
              </Document>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FillSignature;
